// SUB-OFFICE 7 — FORM 2-V, ADJUDICATION PRESSURE ATTENDANCE. Hands on the
// valves at the Vault face while the machine judges a stranger. The needle
// trajectory is a pure function of (seeded spec, inputs): the client renders
// with the SAME integration the server replays, and the log carries ONLY
// inputs — never excursions. The bench's purest anti-cheat case.

import { Vector3 } from 'three';
import {
  GAUGE_BLEED_RATE, GAUGE_FEED_KICK, GAUGE_SIM_DT_MS, gaugeDriftVel,
  type Form2VSpec,
} from '../../shared/physspec';
import { printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

export function runGauges(
  deps: BenchDeps,
  taskId: string,
  spec: Form2VSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM2V', taskId);
    const layer = printLayer();

    // ---- the dressed ceremony: the hall leans in around the manifold ------
    deps.bus.emit('audio:adjudication', { on: true });
    deps.sceneMgr.setDim(0.6, 1.0);
    deps.bus.emit('chyron', `IN CONSIDERATION: ${spec.deliberation.genre}, ${spec.deliberation.designation}. KEEP THE NEEDLES IN BAND.`);
    let camClosing = false;
    let camT = 0;
    const camStart = new Vector3();
    let camCaptured = false;
    deps.sceneMgr.pushCue((dt, camera) => {
      if (!camCaptured) {
        camCaptured = true;
        camStart.copy(camera.position);
      }
      if (!camClosing) {
        camT = Math.min(1, camT + dt / 1.1);
        const k = camT * camT * (3 - 2 * camT);
        camera.position.lerpVectors(camStart, new Vector3(-19, 6.5, 5.5), k);
        camera.lookAt(-29, 4.2, 0);
        return true;
      }
      camT = Math.max(0, camT - dt / 0.5);
      camera.position.lerpVectors(camStart, new Vector3(-19, 6.5, 5.5), camT);
      camera.lookAt(-29, 4.2, 0);
      return camT > 0;
    });

    // ---- the manifold (DOM panel pinned low so the ceremony stays visible) -
    const panel = document.createElement('div');
    panel.style.cssText =
      'position:absolute;left:50%;bottom:24px;transform:translateX(-50%);width:min(96vw,640px);' +
      'background:var(--paper);border:1px solid var(--carbon);box-shadow:0 10px 30px rgba(28,26,23,.5);padding:10px 14px 12px';
    panel.innerHTML =
      `<div class="paper-head letterhead" style="border-bottom:1px solid var(--carbon);padding:0 0 6px;margin-bottom:8px">` +
      `<span>FORM 2-V — GAUGE MANIFOLD</span><span class="tab-num" id="gauge-clock">${spec.duration_s.toFixed(1)}s</span></div>`;
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:12px;justify-content:center';
    panel.appendChild(row);
    layer.appendChild(panel);

    // sim state — the exact integration simulateGauges replays
    const x: number[] = new Array(spec.gauges).fill(0.5);
    const bleedHeld: boolean[] = new Array(spec.gauges).fill(false);
    const feeds: Array<{ g: number; t: number }> = [];
    const half = spec.band_width / 2;
    let li = 0;
    let fi = 0;
    const pendingFeeds: Array<{ g: number; t: number }> = [];

    const canvases: HTMLCanvasElement[] = [];
    for (let g = 0; g < spec.gauges; g++) {
      const col = document.createElement('div');
      col.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:6px';
      const cv = document.createElement('canvas');
      cv.width = 120;
      cv.height = 96;
      cv.style.cssText = 'width:120px;height:96px';
      col.appendChild(cv);
      canvases.push(cv);
      const bleed = document.createElement('button');
      bleed.type = 'button';
      bleed.className = 'slam-btn secondary';
      bleed.style.cssText = 'padding:8px 10px;font-size:10px';
      bleed.textContent = 'HOLD — BLEED';
      const feed = document.createElement('button');
      feed.type = 'button';
      feed.className = 'slam-btn';
      feed.style.cssText = 'padding:8px 10px;font-size:10px';
      feed.textContent = 'FEED +';
      const ctrls = document.createElement('div');
      ctrls.style.cssText = 'display:flex;gap:6px';
      ctrls.append(bleed, feed);
      col.appendChild(ctrls);
      row.appendChild(col);

      bleed.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (!bleedHeld[g]) {
          bleedHeld[g] = true;
          rec.ev('bleed_start', { g });
        }
      });
      const endBleed = (): void => {
        if (bleedHeld[g]) {
          bleedHeld[g] = false;
          rec.ev('bleed_end', { g });
        }
      };
      bleed.addEventListener('pointerup', endBleed);
      bleed.addEventListener('pointercancel', endBleed);
      bleed.addEventListener('pointerleave', endBleed);
      feed.addEventListener('click', () => {
        rec.ev('feed_tap', { g });
        pendingFeeds.push({ g, t: rec.now() });
        deps.bus.emit('audio:tick');
      });
      col.addEventListener('pointerenter', () => rec.ev('focus', { g }));
    }

    rec.ev('mounted', { deliberation_id: spec.deliberation.designation });
    const t0 = performance.now();
    const durMs = spec.duration_s * 1000;
    let simT = 0;
    let raf = 0;
    let finished = false;

    const clockEl = panel.querySelector('#gauge-clock') as HTMLElement;

    const draw = (): void => {
      for (let g = 0; g < spec.gauges; g++) {
        const ctx = canvases[g].getContext('2d');
        if (!ctx) continue;
        ctx.clearRect(0, 0, 120, 96);
        // dial face
        ctx.strokeStyle = '#1C1A17';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(60, 78, 52, Math.PI, 2 * Math.PI);
        ctx.stroke();
        // green band
        ctx.strokeStyle = '#3f7c34';
        ctx.lineWidth = 8;
        ctx.beginPath();
        ctx.arc(60, 78, 48, Math.PI * (1 + (0.5 - half)), Math.PI * (1 + (0.5 + half)));
        ctx.stroke();
        // needle
        const ang = Math.PI * (1 + x[g]);
        const out = Math.abs(x[g] - 0.5) > half;
        ctx.strokeStyle = out ? '#A3271E' : '#1C1A17';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(60, 78);
        ctx.lineTo(60 + Math.cos(ang) * 46, 78 + Math.sin(ang) * 46);
        ctx.stroke();
        ctx.fillStyle = '#1C1A17';
        ctx.font = '9px ui-monospace, monospace';
        ctx.fillText(`NEEDLE ${g + 1}`, 38, 92);
      }
    };

    const finish = (ok: boolean): void => {
      if (finished) return;
      finished = true;
      cancelAnimationFrame(raf);
      for (let g = 0; g < spec.gauges; g++) {
        if (bleedHeld[g]) rec.ev('bleed_end', { g });
      }
      const elapsed = Math.round(performance.now() - t0);
      rec.ev(ok ? 'complete' : 'abandoned', { elapsed });
      deps.bus.emit('audio:adjudication', { on: false });
      deps.sceneMgr.snapDim();
      camClosing = true;
      panel.remove();
      if (ok) {
        deps.bus.emit('chyron', 'NOMINAL. YOU MAY TAKE YOUR HANDS BACK.');
        const built = rec.build(false);
        if (built.valid) {
          resolve(built.log);
          return;
        }
        deps.bus.emit('toast', `The attendance record did not assemble (${built.reason}).`);
      }
      resolve(null);
    };

    const escHandler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        window.removeEventListener('keydown', escHandler);
        finish(false);
      }
    };
    window.addEventListener('keydown', escHandler);

    const frame = (): void => {
      raf = requestAnimationFrame(frame);
      const now = performance.now() - t0;
      // fixed 50ms steps — identical to the server's replay cadence
      while (simT < Math.min(now, durMs)) {
        while (li < spec.lurches.length && spec.lurches[li].t_ms <= simT) {
          const L = spec.lurches[li];
          if (L.gauge < spec.gauges) {
            x[L.gauge] = clamp01(x[L.gauge] + L.mag);
            deps.bus.emit('audio:thunk');
          }
          li++;
        }
        while (fi < pendingFeeds.length && pendingFeeds[fi].t <= simT) {
          const F = pendingFeeds[fi];
          x[F.g] = clamp01(x[F.g] + GAUGE_FEED_KICK);
          feeds.push(F);
          fi++;
        }
        for (let g = 0; g < spec.gauges; g++) {
          let v = gaugeDriftVel(spec, g, simT / 1000);
          if (bleedHeld[g]) v -= GAUGE_BLEED_RATE;
          x[g] = clamp01(x[g] + (v * GAUGE_SIM_DT_MS) / 1000);
        }
        simT += GAUGE_SIM_DT_MS;
      }
      clockEl.textContent = `${Math.max(0, (durMs - now) / 1000).toFixed(1)}s`;
      draw();
      if (now >= durMs) {
        window.removeEventListener('keydown', escHandler);
        finish(true);
      }
    };
    raf = requestAnimationFrame(frame);
  });
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
